import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Form, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { confirmPasswordReset, createPasswordReset } from '../../contexts';
import { allPaths } from '../../routes/Paths';
import {
  InputWrapper,
  Loading,
  LoginFormHeader,
  LoginFormFooter,
  LoginFormButton
} from '../atoms';
import { NewPasswordFields } from '../molecules';

const Error = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.error};
    padding: 8px 8px 8px 0;
    display: block;
  `
);

const resetPasswordSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Please enter your email address.' })
    .email('Please enter a valid email address.')
});

const confirmResetPasswordSchema = z.object({
  confirmationCode: z.string().optional(),
  newPassword: z
    .string()
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
      'Please enter a valid password'
    ),
  confirmPassword: z.string().min(1, 'Please confirm your password')
});

type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>;
type ConfirmResetPasswordSchema = z.infer<typeof confirmResetPasswordSchema>;

const CreatePasswordResetForm = ({ onResetSubmit = () => undefined }) => {
  const [error, setError] = useState(undefined);
  const [unverified, setUnverified] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema)
  });

  const submitCreate: SubmitHandler<ResetPasswordSchema> = async (
    data: any
  ) => {
    try {
      setError(undefined);
      setUnverified(false);
      const { email } = data;
      const response = await createPasswordReset(email);
      const { message, result: { deliveryMedium, destination } = {} } =
        response;
      onResetSubmit({
        email,
        message,
        deliveryMedium,
        destination
      });

      toast.success(
        `If ${destination} exists in our system, we'll send you a confirmation code. Please check your ${(
          deliveryMedium ?? 'email'
        ).toLowerCase()}`
      );
    } catch (err) {
      console.error('Failed to create password reset', err);
      const { message = 'Unknown error', data: { errorCode } = {} } = err;
      if (errorCode === 'email_not_verified') {
        setUnverified(true);
      } else {
        setError(message);
      }
    }
  };

  return (
    <Form onSubmit={handleSubmit(submitCreate)}>
      {unverified && (
        <LoginFormHeader>
          <Error>
            Your email address has not been verified. Please contact your
            Administrator.
          </Error>
        </LoginFormHeader>
      )}
      <InputWrapper
        name="email"
        type="email"
        label="Email"
        required
        register={register}
        errorMessage={errors.email?.message}
      />
      <LoginFormFooter
        horizontalSpacing="10px"
        verticalSpacing="0px"
        justify="center"
      >
        <LoginFormButton type="submit" disabled={isSubmitting}>
          Send confirmation code
          {isSubmitting && <Loading inline />}
        </LoginFormButton>
      </LoginFormFooter>
      {error && (
        <Error>
          Failed to request password reset.
          <br />
          {error}
        </Error>
      )}
    </Form>
  );
};

const ConfirmPasswordResetForm = ({ resetInfo = {} }) => {
  const [error, setError] = useState(undefined);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<ConfirmResetPasswordSchema>({
    resolver: zodResolver(confirmResetPasswordSchema)
  });
  const { email } = resetInfo;

  const submitConfirm: SubmitHandler<ConfirmResetPasswordSchema> = async (
    data: any
  ) => {
    try {
      setError('');
      setSubmitted(true);
      // password issues still to resolve?
      if (passwordErrors?.newPassword || passwordErrors?.confirmPassword) {
        return false;
      }

      const { confirmationCode, newPassword } = data;
      await confirmPasswordReset(email, confirmationCode, newPassword);

      toast.success(
        'Successfully reset password. Please login with your new password'
      );
      navigate(allPaths.LOGIN.path);
    } catch (err) {
      console.error('Failed to confirm password reset', err);
      setError(
        'The confirmation code provided for the challenge request does not match what was expected'
      );
    }
  };

  return (
    <Form onSubmit={handleSubmit(submitConfirm)} noValidate={true}>
      <InputWrapper type="email" label="Email" value={email} disabled />
      <InputWrapper
        name="confirmationCode"
        type="text"
        label="Confirmation Code"
        autoFill={false}
        required
        register={register}
        errorMessage={errors.confirmationCode?.message || error}
      />
      <NewPasswordFields
        register={register}
        errors={
          submitted
            ? {
                ...errors,
                newPassword: {
                  ...errors.newPassword,
                  message: passwordErrors?.newPassword
                },
                confirmPassword: {
                  ...errors.confirmPassword,
                  message: passwordErrors?.confirmPassword
                }
              }
            : errors
        }
        onChange={(state, pwdErrors) => {
          setPasswordErrors(pwdErrors);
        }}
      />
      <LoginFormFooter
        horizontalSpacing="10px"
        verticalSpacing="0px"
        justify="center"
      >
        <LoginFormButton
          type="submit"
          disabled={
            errors.confirmationCode ||
            errors.newPassword ||
            errors.confirmPassword ||
            passwordErrors.newPassword ||
            passwordErrors.confirmPassword ||
            isSubmitting
          }
        >
          Reset Password
          {isSubmitting && <Loading inline />}
        </LoginFormButton>
      </LoginFormFooter>
    </Form>
  );
};

export const ResetPasswordPage = () => {
  const [resetInfo, setResetInfo] = useState(undefined);

  return (
    <div>
      <LoginFormHeader>
        <h1>Request new password</h1>
        <p>
          {resetInfo
            ? `Please check your email for a code to confirm your password reset`
            : 'Enter your email and we’ll send a code to confirm your password reset'}
        </p>
      </LoginFormHeader>
      {resetInfo ? (
        <ConfirmPasswordResetForm resetInfo={resetInfo} />
      ) : (
        <CreatePasswordResetForm onResetSubmit={(info) => setResetInfo(info)} />
      )}
    </div>
  );
};
